/**
 * Cross-view helpers for the Data Explorer configs: the shared
 * "Has open issues" toggle, the Issues-column last-flagged-age wrapper,
 * and param builders for the browse-only filter keys.
 */
import React from 'react'
import { Stack, Tooltip, Typography } from '@mui/material'
import type { ExceptionColumnDef, ExceptionFilterDef } from '../exceptions/types'
import { renderErrorCount } from '../exceptions/shared/productRenderers'
import { actionableEntries } from '../exceptions/shared/issuesState'
import { formatDateTime, formatDetectedAge } from '../../utils/inspectorFormatters'

export const HAS_ISSUES_FILTER_KEY = 'has_issues'

export const hasIssuesFilter: ExceptionFilterDef = {
  key: HAS_ISSUES_FILTER_KEY,
  type: 'toggle',
  label: 'Has open issues',
  defaultEnabled: false,
}

export const buildHasIssuesParam = (
  filterValues: Record<string, unknown> | undefined,
): Record<string, boolean> =>
  filterValues?.[HAS_ISSUES_FILTER_KEY] === true ? { has_issues: true } : {}

/**
 * Wrap an Issues column so the cell also reports when the row's most
 * recent open issue was last seen - a muted "57d ago" line under the
 * count chip - but ONLY when the row actually has open issues. Rows with
 * no issues keep the bare chip (blank cell), so the recency value never
 * shows where it has no meaning. The exact timestamp rides the hover
 * tooltip. This replaces the standalone "Issue Detected" column: a date
 * is only useful alongside an issue, so it belongs with the count.
 */
export const withDetectedAge = (
  base: ExceptionColumnDef,
): ExceptionColumnDef => ({
  ...base,
  render: (value, row, ctx) => {
    const chip = renderErrorCount(value, row, ctx)
    const errors = (Array.isArray(value) ? value : row.errors) ?? []
    const detectedAt = row.issues_last_detected_at as string | null | undefined
    if (actionableEntries(errors).length === 0 || !detectedAt) return chip
    return React.createElement(
      Stack,
      // Centre the chip + age caption to match the Issues column's centred
      // body cells (issuesColumn.ts ``align: 'center'``); flex-end would pin
      // them right and reintroduce the corner-badge crowding.
      { spacing: 0.25, alignItems: 'center' },
      chip,
      React.createElement(Tooltip, {
        title: formatDateTime(detectedAt),
        children: React.createElement(
          Typography,
          {
            variant: 'caption',
            color: 'text.secondary',
            // Single line, a touch smaller than the chip with tightened
            // tracking - the narrow Issues column is too narrow for "17d ago"
            // to wrap.
            sx: {
              lineHeight: 1.1,
              whiteSpace: 'nowrap',
              fontSize: '0.65rem',
              letterSpacing: '-0.02em',
            },
          },
          formatDetectedAge(detectedAt),
        ),
      }),
    )
  },
})

/** Last-30-days [start, end] ISO date pair for the sales default range. */
export const last30DaysRange = (): [string, string] => {
  const end = new Date()
  const start = new Date()
  start.setDate(start.getDate() - 30)
  const iso = (d: Date) => d.toISOString().slice(0, 10)
  return [iso(start), iso(end)]
}
